/**
 * Contextual help map: app URL pattern → guide slug.
 *
 * The Header's contextual help button uses this to open the most relevant
 * guide for whatever page the user is currently on. Patterns are matched
 * top-down with the FIRST match winning, so put more specific patterns
 * (with sub-paths) BEFORE catchalls.
 *
 * If no entry matches, the button drops the user on /help (the catalog).
 */

import { canRoleReadGuide, getGuideBySlug } from "./guides";

export interface ContextualHelpEntry {
  /** RegExp matched against pathname (e.g. /^\/classes(\/|$)/). */
  pattern: RegExp;
  /** Slug from guides.ts. */
  slug: string;
}

export const CONTEXTUAL_HELP: ContextualHelpEntry[] = [
  // Curriculum
  { pattern: /^\/curriculum\/lessons/, slug: "curriculum-overview" },
  { pattern: /^\/curriculum\/subjects\/[^/]+/, slug: "build-unit-and-lessons" },
  { pattern: /^\/curriculum\/subjects/, slug: "curriculum-overview" },
  // The unit page is where lessons are added.
  { pattern: /^\/curriculum\/units\/[^/]+/, slug: "build-unit-and-lessons" },
  { pattern: /^\/curriculum\/lesson-plan-templates/, slug: "lesson-plans" },
  { pattern: /^\/curriculum\/grade-levels/, slug: "grade-levels" },
  { pattern: /^\/curriculum/, slug: "curriculum-overview" },

  // Academics nested under a class
  { pattern: /^\/classes\/[^/]+\/lesson-progress/, slug: "lesson-progress" },
  { pattern: /^\/classes\/[^/]+\/assignments/, slug: "assignments-and-gradebook" },
  { pattern: /^\/classes\/[^/]+\/gradebook/, slug: "assignments-and-gradebook" },
  { pattern: /^\/classes\/[^/]+\/assessments/, slug: "assessments-quizzes" },
  { pattern: /^\/lesson-progress/, slug: "lesson-progress" },
  { pattern: /^\/assignments/, slug: "assignments-and-gradebook" },
  { pattern: /^\/classes\/[^/]+\/roster/, slug: "class-roster-and-placement" },
  { pattern: /^\/classes\/[^/]+\/placement/, slug: "class-roster-and-placement" },
  // A class's schedules feed attendance sessions. Also reached from
  // Attendance, so this must sit above the /attendance rules below.
  { pattern: /^\/(attendance\/)?classes\/[^/]+\/schedules/, slug: "generate-sessions" },
  { pattern: /^\/classes\/templates/, slug: "class-templates" },
  { pattern: /^\/classes\/clone/, slug: "clone-class" },
  { pattern: /^\/classes\/[^/]+\/clone/, slug: "clone-class" },
  { pattern: /^\/classes\/new/, slug: "create-class" },
  { pattern: /^\/classes\/[^/]+/, slug: "create-class" },
  { pattern: /^\/classes/, slug: "create-class" },

  // Behavior + report cards + messaging
  { pattern: /^\/behavior/, slug: "behavior-incidents" },
  { pattern: /^\/reports\/report-cards/, slug: "report-cards" },
  { pattern: /^\/messages/, slug: "messaging-overview" },

  // Hifdh
  { pattern: /^\/hifdh\/v2\/classes\/[^/]+\/months\//, slug: "hifdh-month-report-cards" },
  { pattern: /^\/hifdh\/v2\/(students|classes)\/[^/]+\/certificates/, slug: "hifdh-certificates" },
  { pattern: /^\/hifdh\/v2/, slug: "hifdh-daily-rating" },
  { pattern: /^\/hifdh\/report-card/, slug: "hifdh-reports" },
  { pattern: /^\/hifdh/, slug: "hifdh-overview" },

  // Billing
  { pattern: /^\/invoices\/batch/, slug: "generate-invoices" },
  { pattern: /^\/invoices\/generate/, slug: "generate-invoices" },
  // Invoice detail: void, credit memos, late-fee removal.
  { pattern: /^\/invoices\/[^/]+/, slug: "correcting-invoices" },
  { pattern: /^\/invoices/, slug: "generate-invoices" },
  // Specific paths must come before /payments so /families/.../payment-methods
  // is caught here, not by the bare /payments rule.
  { pattern: /^\/families\/[^/]+\/payment-methods/, slug: "payment-methods" },
  // Before the /families catch-all under Admissions / people.
  { pattern: /^\/families\/[^/]+\/statement/, slug: "family-statements" },
  { pattern: /^\/parent\/payment-methods/, slug: "payment-methods" },
  { pattern: /^\/reports\/autopay-enrollment/, slug: "understanding-autopay" },
  { pattern: /^\/reports\/failed-payments/, slug: "pausing-autopay" },
  { pattern: /^\/reports\/billing-collections/, slug: "billed-vs-collected-report" },
  { pattern: /^\/reports\/family-statement/, slug: "family-statements" },
  { pattern: /^\/reports\/hifdh-progress/, slug: "hifdh-reports" },
  { pattern: /^\/payments\/record/, slug: "record-payments" },
  // Payment detail is where a refund is issued.
  { pattern: /^\/payments\/[^/]+/, slug: "issuing-refunds" },
  { pattern: /^\/payments/, slug: "record-payments" },
  { pattern: /^\/fees/, slug: "setup-fee-types" },
  { pattern: /^\/discounts/, slug: "manage-discounts" },
  { pattern: /^\/zakat-fund/, slug: "zakat-fund" },
  { pattern: /^\/sponsor-fund/, slug: "sponsor-funding" },
  // /sponsor/:token is the public campaign sponsor page (no-login), not TEFA.
  { pattern: /^\/sponsor\//, slug: "fundraising-campaigns" },
  { pattern: /^\/campaigns/, slug: "fundraising-campaigns" },
  { pattern: /^\/potlucks?/, slug: "potluck-signups" },
  { pattern: /^\/statements/, slug: "family-statements" },

  // Operations
  { pattern: /^\/attendance\/sessions/, slug: "generate-sessions" },
  { pattern: /^\/attendance/, slug: "take-attendance" },
  { pattern: /^\/timecard/, slug: "staff-time-clock" },
  { pattern: /^\/admin\/timecard/, slug: "staff-time-clock" },

  // Admissions / people
  { pattern: /^\/applications/, slug: "manage-applications" },
  { pattern: /^\/families/, slug: "add-family-students" },
  { pattern: /^\/students/, slug: "add-family-students" },
  { pattern: /^\/guardians/, slug: "add-family-students" },
  { pattern: /^\/teachers/, slug: "assign-teachers" },
  { pattern: /^\/enrollments\/paused/, slug: "manage-enrollments" },
  { pattern: /^\/enrollments/, slug: "manage-enrollments" },

  // Portals
  // Lesson Plans is a teacher route that staff open too (Academics).
  { pattern: /^\/teacher\/lesson-plans/, slug: "lesson-plans" },
  { pattern: /^\/teacher\//, slug: "teacher-portal-overview" },
  { pattern: /^\/parent\/lesson-progress/, slug: "lesson-progress" },
  { pattern: /^\/parent\/hifdh-progress/, slug: "hifdh-parents" },
  { pattern: /^\/parent\//, slug: "parent-portal-mobile" },

  // Reports + admin + settings
  { pattern: /^\/reports/, slug: "reports-overview" },
  { pattern: /^\/analytics/, slug: "reports-overview" },
  { pattern: /^\/users/, slug: "manage-users" },
  { pattern: /^\/admin\/audit-log/, slug: "audit-log" },
  { pattern: /^\/admin\/tenant-settings/, slug: "tenant-settings" },
  { pattern: /^\/admin\/stripe-config/, slug: "tenant-settings" },
  { pattern: /^\/notifications/, slug: "setup-notifications" },

  // Your account
  { pattern: /^\/profile/, slug: "signing-in" },
  { pattern: /^\/change-password/, slug: "signing-in" },

  // Calendar / campuses
  { pattern: /^\/calendar\/academic-years/, slug: "academic-years" },
  // Terms are set up inside an academic year.
  { pattern: /^\/calendar\/terms/, slug: "academic-years" },
  { pattern: /^\/calendar/, slug: "setup-school-calendar" },
  { pattern: /^\/campuses/, slug: "create-campus" },

  // Dashboard
  { pattern: /^\/dashboard/, slug: "dashboard-overview" },
];

/**
 * Pages parents share with staff, where the staff guide (logging incidents,
 * generating report cards) is the wrong answer for a parent. Checked before
 * CONTEXTUAL_HELP when the user is a parent.
 */
export const PARENT_CONTEXTUAL_HELP: ContextualHelpEntry[] = [
  { pattern: /^\/behavior/, slug: "parent-portal-mobile" },
  { pattern: /^\/reports\/report-cards/, slug: "parent-portal-mobile" },
  { pattern: /^\/settings\/notifications/, slug: "parent-portal-mobile" },
];

/**
 * The same for teachers: pages they share with the office whose guide is
 * office-only would otherwise send them to the catalog.
 */
export const TEACHER_CONTEXTUAL_HELP: ContextualHelpEntry[] = [
  { pattern: /^\/settings\/notifications/, slug: "teacher-portal-overview" },
];

/** Notification settings, reached from Profile by every role that has them. */
const ACCOUNT_CONTEXTUAL_HELP: ContextualHelpEntry[] = [
  { pattern: /^\/settings\/notifications/, slug: "setup-notifications" },
];

/**
 * Resolve the most relevant guide slug for a given pathname (and, when
 * known, the user's role). Returns `null` if nothing matches; the caller
 * can fall back to /help.
 *
 * With a role, a match whose guide that role can't read is skipped and the
 * next match tried, so a parent on /parent/payment-methods (an office guide)
 * falls through to the parent portal guide rather than to a guide they
 * can't open.
 */
export function resolveContextualSlug(
  pathname: string,
  role?: string | null,
): string | null {
  const roleEntries =
    role === "parent"
      ? PARENT_CONTEXTUAL_HELP
      : role === "teacher"
        ? TEACHER_CONTEXTUAL_HELP
        : [];
  const entries = [...roleEntries, ...ACCOUNT_CONTEXTUAL_HELP, ...CONTEXTUAL_HELP];
  for (const entry of entries) {
    if (!entry.pattern.test(pathname)) continue;
    if (role === undefined) return entry.slug;
    const guide = getGuideBySlug(entry.slug);
    if (guide && canRoleReadGuide(guide, role)) return entry.slug;
  }
  return null;
}
